import { useState, useEffect } from 'react';
import { settingsApi } from '../api';
import toast from 'react-hot-toast';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';
const BASE_URL = API_URL.replace('/api/v1', '');

export default function GeneralSettings() {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    company_name: '',
    company_address: '',
    company_number: '',
    notes: '',
    description: '',
    company_logo: null,
    whatsapp_message: false,
    whatsapp_url: '',
    whatsapp_api_key: '',
  });

  const [logoFile, setLogoFile] = useState(null);

  // Fetch settings on mount
  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const res = await settingsApi.get();
      if (res.data && res.data.data) {
        const data = res.data.data;
        setForm({
          company_name: data.company_name || '',
          company_address: data.company_address || '',
          company_number: data.company_number || '',
          notes: data.notes || '',
          description: data.description || '',
          company_logo: data.company_logo ? `${BASE_URL}${data.company_logo}` : null,
          whatsapp_message: data.whatsapp_message || false,
          whatsapp_url: data.whatsapp_url || '',
          whatsapp_api_key: data.whatsapp_api_key || '',
        });
      }
    } catch (err) {
      console.error('Failed to load settings:', err);
    }
    setLoading(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      setForm((prev) => ({
        ...prev,
        company_logo: URL.createObjectURL(file),
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('company_name', form.company_name);
      formData.append('company_address', form.company_address);
      formData.append('company_number', form.company_number);
      formData.append('notes', form.notes);
      formData.append('description', form.description);
      formData.append('whatsapp_message', form.whatsapp_message);
      formData.append('whatsapp_url', form.whatsapp_url);
      formData.append('whatsapp_api_key', form.whatsapp_api_key);
      if (logoFile) {
        formData.append('company_logo', logoFile);
      }

      await settingsApi.update(formData);
      toast.success('Settings saved successfully');

      // Refresh settings to get the updated logo URL
      fetchSettings();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save settings');
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading-spinner">Loading...</div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1 className="page-title">General Settings</h1>
      </div>

      <div className="settings-card">
        <form onSubmit={handleSubmit}>
          {/* Company Logo Section */}
          <div className="settings-section">
            <h3 className="settings-section-title">Company Logo</h3>
            <div className="logo-upload-container">
              <div className="logo-preview-wrapper">
                {form.company_logo ? (
                  <img
                    src={form.company_logo}
                    alt="Company Logo"
                    className="logo-preview-img"
                  />
                ) : (
                  <div className="logo-placeholder">
                    <span>No Logo</span>
                  </div>
                )}
              </div>
              <div className="logo-upload-controls">
                <input
                  type="file"
                  id="company_logo"
                  accept="image/*"
                  onChange={handleLogoChange}
                  className="form-control-file"
                />
                <p className="form-help-text">
                  Recommended size: 200x200px. Supported formats: JPG, PNG, GIF
                </p>
              </div>
            </div>
          </div>

          {/* Company Information Section */}
          <div className="settings-section">
            <h3 className="settings-section-title">Company Information</h3>

            <div className="form-group">
              <label className="form-label">Company Name</label>
              <input
                type="text"
                name="company_name"
                className="form-control"
                value={form.company_name}
                onChange={handleChange}
                placeholder="Enter company name"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Company Phone Number</label>
              <input
                type="text"
                name="company_number"
                className="form-control"
                value={form.company_number}
                onChange={handleChange}
                placeholder="Enter phone number"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Company Address</label>
              <textarea
                name="company_address"
                className="form-control"
                value={form.company_address}
                onChange={handleChange}
                rows={3}
                placeholder="Enter company address"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea
                name="description"
                className="form-control"
                value={form.description}
                onChange={handleChange}
                rows={3}
                placeholder="Enter description"
              />
            </div>
          </div>

          {/* Additional Settings Section */}
          <div className="settings-section">
            <h3 className="settings-section-title">Additional Settings</h3>

            <div className="toggle-group">
              <div className="toggle-item">
                <div className="toggle-info">
                  <label className="toggle-label">WhatsApp Message</label>
                  <p className="toggle-description">Enable to send WhatsApp messages to customers</p>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={form.whatsapp_message}
                    onChange={(e) => setForm((prev) => ({ ...prev, whatsapp_message: e.target.checked }))}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              {form.whatsapp_message && (
                <div className="whatsapp-fields">
                  <div className="form-group">
                    <label className="form-label">WhatsApp URL</label>
                    <input
                      type="text"
                      name="whatsapp_url"
                      className="form-control"
                      value={form.whatsapp_url}
                      onChange={handleChange}
                      placeholder="Enter WhatsApp API URL"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">WhatsApp API Key</label>
                    <input
                      type="text"
                      name="whatsapp_api_key"
                      className="form-control"
                      value={form.whatsapp_api_key}
                      onChange={handleChange}
                      placeholder="Enter WhatsApp API Key"
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">Notes</label>
              <textarea
                name="notes"
                className="form-control"
                value={form.notes}
                onChange={handleChange}
                rows={4}
                placeholder="Add any additional notes or information"
              />
            </div>
          </div>

          {/* Submit Button */}
          <div className="settings-actions">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </form>
      </div>

      <style>{`
        .settings-card {
          background: #fff;
          border-radius: 8px;
          padding: 24px;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        .settings-section {
          margin-bottom: 32px;
          padding-bottom: 24px;
          border-bottom: 1px solid #e5e7eb;
        }

        .settings-section:last-of-type {
          border-bottom: none;
          margin-bottom: 24px;
        }

        .settings-section-title {
          font-size: 18px;
          font-weight: 600;
          color: #1f2937;
          margin-bottom: 20px;
        }

        .logo-upload-container {
          display: flex;
          gap: 24px;
          align-items: flex-start;
        }

        .logo-preview-wrapper {
          width: 150px;
          height: 150px;
          border: 2px dashed #d1d5db;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
          background: #f9fafb;
        }

        .logo-preview-img {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }

        .logo-placeholder {
          color: #9ca3af;
          font-size: 14px;
          
        }

        .logo-upload-controls {
          flex: 1;
        }

        .form-control-file {
          display: block;
          width: 100%;
          padding: 8px 12px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          background: #fff;
          font-size: 14px;
        }

        .form-help-text {
          margin-top: 8px;
          font-size: 12px;
          color: #6b7280;
        }

        .form-group {
          margin-bottom: 20px;
        }

        .form-label {
          display: block;
          font-size: 14px;
          font-weight: 500;
          color: #374151;
          margin-bottom: 6px;
        }

        .form-control {
          display: block;
          width: 100%;
          padding: 10px 12px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 14px;
          transition: border-color 0.15s ease-in-out;
        }

        .form-control:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .settings-actions {
          display: flex;
          justify-content: flex-end;
        }

        .btn {
          padding: 10px 20px;
          border-radius: 6px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.15s ease-in-out;
        }

        .btn-primary {
          background: #3b82f6;
          color: #fff;
          border: none;
        }

        .btn-primary:hover {
          background: #2563eb;
        }

        .btn-primary:disabled {
          background: #93c5fd;
          cursor: not-allowed;
        }

        .loading-spinner {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 40px;
          color: #6b7280;
        }

        .toggle-group {
          margin-bottom: 24px;
        }

        .toggle-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 16px;
          background: #f9fafb;
          border-radius: 8px;
          border: 1px solid #e5e7eb;
        }

        .toggle-info {
          flex: 1;
        }

        .toggle-label {
          display: block;
          font-size: 14px;
          font-weight: 600;
          color: #1f2937;
          margin-bottom: 4px;
        }

        .toggle-description {
          font-size: 12px;
          color: #6b7280;
          margin: 0;
        }

        .toggle-switch {
          position: relative;
          display: inline-block;
          width: 52px;
          height: 28px;
          flex-shrink: 0;
        }

        .toggle-switch input {
          opacity: 0;
          width: 0;
          height: 0;
        }

        .toggle-slider {
          position: absolute;
          cursor: pointer;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: #ccc;
          transition: 0.3s;
          border-radius: 28px;
        }

        .toggle-slider:before {
          position: absolute;
          content: "";
          height: 22px;
          width: 22px;
          left: 3px;
          bottom: 3px;
          background-color: white;
          transition: 0.3s;
          border-radius: 50%;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }

        .toggle-switch input:checked + .toggle-slider {
          background-color: #25D366;
        }

        .toggle-switch input:checked + .toggle-slider:before {
          transform: translateX(24px);
        }

        .toggle-switch input:focus + .toggle-slider {
          box-shadow: 0 0 0 3px rgba(37, 211, 102, 0.2);
        }

        .whatsapp-fields {
          margin-top: 16px;
          padding: 16px;
          background: #fff;
          border-radius: 8px;
          border: 1px solid #e5e7eb;
        }
      `}</style>
    </div>
  );
}
